import type { ReactNode } from 'react';

/**
 * The four shapes a help text is allowed to have.
 *
 * Its own module rather than part of Help.tsx, because topics.tsx imports these at module
 * scope (the bodies are JSX literals, not render functions) and Help.tsx imports the
 * topics — putting both halves in one file makes that cycle initialise in the wrong order
 * and the prose components come back undefined.
 *
 * Deliberately few: a help text that needs a fifth element is a help text that has started
 * being a manual. Nothing here takes a className, so no topic can style itself apart from
 * the others.
 */

export function P({ children }: { children: ReactNode }) {
  return <p className="text-[13px] leading-relaxed text-[var(--color-ink)]">{children}</p>;
}

/** A list of facts. Not a list of steps — the console is not walked through here. */
export function L({ items }: { items: ReactNode[] }) {
  return (
    <ul className="list-disc space-y-1 pl-5 text-[13px] leading-relaxed">
      {items.map((x, i) => <li key={i}>{x}</li>)}
    </ul>
  );
}

/**
 * Term and meaning.
 *
 * Most questions in this console are "what does this word on screen mean", and answering
 * that in prose buries the word in a sentence. As a <dl> it stays scannable, which is how
 * someone reads help while a dialog waits behind it.
 */
export function Terms({ items }: { items: [string, ReactNode][] }) {
  return (
    <dl className="space-y-2 text-[13px] leading-relaxed">
      {items.map(([term, meaning]) => (
        <div key={term}>
          <dt className="font-medium">{term}</dt>
          <dd className="text-[var(--color-muted)]">{meaning}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * The one thing that costs money or credibility if it is skipped.
 *
 * Tinted like Banner kind="info" rather than like an error: a help text has nothing
 * urgent in it by definition, and a red box in documentation trains people to ignore red
 * boxes everywhere else.
 */
export function Note({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-[3px] border border-[var(--color-note-line)] bg-[var(--color-note)]
                    px-3 py-2 text-[13px] leading-relaxed text-[var(--color-note-ink)]">
      {children}
    </div>
  );
}

/** Something the reader will see spelled exactly this way on screen or in their HTML. */
export function C({ children }: { children: ReactNode }) {
  return (
    <code className="rounded-[2px] bg-[var(--color-sunken)] px-1 py-px font-mono text-[11px]">
      {children}
    </code>
  );
}
